import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../assets/RCS-Logo-White.png";
import CollapsedLogo from "../assets/RCS-Logo-White.png";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/auth-slice";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import clsx from "clsx";
import { API_BASE_URL, authFetch } from "../config/api";

const normalizeStatus = (value) => String(value || "").toLowerCase();

const RequestorSidebar = ({ forceExpanded = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const { first_name = "", last_name = "" } = user || {};
  const [openRequests, setOpenRequests] = useState(true);
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(window.innerWidth >= 1024);
  });
  const [textVisible, setTextVisible] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(window.innerWidth >= 1024);
  });
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      const width = window.innerWidth;
      const large = Boolean(width >= 1025);
      setIsLargeScreen(large);
      setTextVisible(large);
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchPendingRequestCount = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/api/accounting/requests`, {
          headers: {
            "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        });
        const json = await response.json();
        const rows = Array.isArray(json?.data) ? json.data : [];
        const pendingCount = rows.filter((row) =>
          ["pending", "partially_approved", "submitted"].includes(normalizeStatus(row.status))
        ).length;
        setPendingRequestCount(pendingCount);
      } catch (error) {
        console.error("Error fetching pending request count:", error);
      }
    };

    fetchPendingRequestCount();
  }, []);

  const isActive = (path) => location.pathname === path;
  const isJoLedgerActive = location.pathname.startsWith("/requestor/jo-ledger");
  const isProfileActive = location.pathname === "/requestor/profile";
  const isExpanded = forceExpanded || isLargeScreen;
  const isTextVisible = forceExpanded || textVisible;

  const handleLogout = () => {
    dispatch(logoutUser()).then((data) => {
      const message = data?.payload?.message;
      if (data?.payload?.success) {
        toast.success(message, { position: "top-right" });
      } else {
        toast.error(message, { position: "top-right" });
      }
    });
  };

  const handleLogoutConfirmation = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md mx-4 w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="fas fa-sign-out-alt text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-blue mb-2">Confirm Logout</h2>
              <p className="text-grey">Are you sure you want to sign out of your account?</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-grey font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  onClose();
                }}
                className="flex-1 px-6 py-3 bg-red text-white font-semibold rounded-xl hover:bg-red/90 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      ),
    });
  };

  const displayName = `${first_name || ""} ${last_name || ""}`.trim() || user?.user_name || "Requestor";

  return (
    <div className="flex font-poppins h-full">
      <div
        className={clsx(
          "h-full bg-blue text-white transition-all duration-500 ease-out backdrop-blur-xl shadow-2xl border-r border-white/10 relative",
          isExpanded ? "w-72" : "w-20"
        )}
        style={{ willChange: "transform", transform: "translateZ(0)" }}
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-4 w-32 h-32 bg-yellow/5 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-32 left-4 w-24 h-24 bg-white/5 rounded-full blur-xl animate-pulse delay-1000"></div>
        </div>

        <div className={`relative flex items-center justify-center p-6 border-b border-white/10 backdrop-blur-sm ${isExpanded ? "h-28" : "h-20"}`}>
          <img
            src={isLargeScreen ? Logo : CollapsedLogo}
            alt="Logo"
            className={`transition-all duration-500 ${
              isExpanded ? "w-44 h-16 object-contain" : "w-12 h-12 object-contain"
            }`}
          />
        </div>

        <nav className="mt-8 px-4 space-y-2 overflow-y-auto pb-32" style={{ maxHeight: "calc(100vh - 16rem)" }}>
          <Link
            to="/requestor/dashboard"
              className={`group relative flex items-center gap-4 px-5 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isExpanded ? "justify-start" : "justify-center"
            } ${
              isActive("/requestor/dashboard")
                ? "bg-gradient-to-r from-yellow to-yellow/90 text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
          >
            <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
              isActive("/requestor/dashboard") ? "text-blue" : "text-white hover:text-yellow"
            }`}>
              <i className="fa-solid fa-chart-line text-lg"></i>
              {isActive("/requestor/dashboard") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>
            {isTextVisible && (
              <span className={`font-semibold transition-all duration-300 ${
                isActive("/requestor/dashboard") ? "text-blue" : "text-white group-hover:text-yellow"
              }`}>
                Dashboard
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={() => setOpenRequests((v) => !v)}
            className="w-full mt-2 flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wide text-white/70 hover:text-white transition-colors"
          >
            <i className={`fa-solid fa-chevron-${openRequests ? "down" : "right"} text-[10px]`}></i>
            {isTextVisible && <span>Requests</span>}
          </button>

          {openRequests && (
            <div className="space-y-2">
              <Link
                to="/requestor/request-create"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isExpanded ? "justify-start" : "justify-center"
                } ${
                  isActive("/requestor/request-create")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/requestor/request-create") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-file-circle-plus text-lg"></i>
                </div>
                {isTextVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/requestor/request-create") ? "text-blue" : "text-white group-hover:text-yellow"
                  }`}>
                    Request Creation
                  </span>
                )}
              </Link>

              <Link
                to="/requestor/request-summary"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isExpanded ? "justify-start" : "justify-center"
                } ${
                  isActive("/requestor/request-summary")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/requestor/request-summary") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-file-lines text-lg"></i>
                </div>
                {isTextVisible && (
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold transition-all duration-300 ${
                      isActive("/requestor/request-summary") ? "text-blue" : "text-white group-hover:text-yellow"
                    }`}>
                      Request Summary
                    </span>
                    {pendingRequestCount > 0 && (
                      <span
                        className={clsx(
                          "inline-flex min-w-6 items-center justify-center rounded-full px-2 py-0.5 text-[11px] font-bold",
                          isActive("/requestor/request-summary")
                            ? "bg-blue text-white"
                            : "bg-yellow text-blue"
                        )}
                        title={`${pendingRequestCount} request${pendingRequestCount === 1 ? "" : "s"} pending approval`}
                      >
                        {pendingRequestCount}
                      </span>
                    )}
                  </div>
                )}
              </Link>

              <Link
                to="/requestor/liquidation-report"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isExpanded ? "justify-start" : "justify-center"
                } ${
                  isActive("/requestor/liquidation-report")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/requestor/liquidation-report") ? "text-blue" : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-receipt text-lg"></i>
                </div>
                {isTextVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isActive("/requestor/liquidation-report") ? "text-blue" : "text-white group-hover:text-yellow"
                  }`}>
                    Liquidation Report
                  </span>
                )}
              </Link>

              <Link
                to="/requestor/jo-ledger"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                  isExpanded ? "justify-start" : "justify-center"
                } ${
                  isJoLedgerActive
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isJoLedgerActive
                    ? "text-blue"
                    : "text-white group-hover:text-yellow"
                }`}>
                  <i className="fa-solid fa-table-list text-lg"></i>
                </div>
                {isTextVisible && (
                  <span className={`font-semibold transition-all duration-300 ${
                    isJoLedgerActive
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}>
                    JO Ledger
                  </span>
                )}
              </Link>
            </div>
          )}
        </nav>

        <div className="absolute bottom-0 left-0 w-full border-t border-white/10 backdrop-blur-sm">
          <div className="p-6 space-y-4">
            <Link
              to="/requestor/profile"
              className={clsx(
                "group flex w-full items-center gap-3 rounded-2xl px-3 py-3 transition-all duration-300",
                "hover:bg-white/10 hover:shadow-lg",
                isProfileActive && "bg-white/10 shadow-lg"
              )}
              title="Open profile"
            >
              <div
                className={clsx(
                  "flex h-10 w-10 items-center justify-center rounded-2xl transition-all duration-300",
                  isProfileActive
                    ? "bg-yellow text-blue"
                    : "bg-white/10 text-white group-hover:bg-white/20 group-hover:text-yellow"
                )}
              >
                <i className="fa-solid fa-user text-sm"></i>
              </div>
              {isTextVisible && (
                <div className="min-w-0">
                  <p className="font-bold text-white text-sm truncate group-hover:text-yellow transition-colors duration-300">
                    {displayName}
                  </p>
                  <p className="text-xs text-white/70 truncate">Requestor Portal</p>
                </div>
              )}
            </Link>

            <button
              className={clsx(
                "group relative flex items-center gap-3 w-full px-4 py-3",
                "bg-red hover:bg-red/90",
                "text-white rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl",
                isExpanded ? "justify-start" : "justify-center"
              )}
              onClick={handleLogoutConfirmation}
            >
              <div className="relative z-10 flex items-center gap-3">
                <div className="flex items-center justify-center w-5 h-5">
                  <i className="fa-solid fa-right-from-bracket text-sm group-hover:text-yellow transition-colors duration-300"></i>
                </div>
                {textVisible && (
                  <span className="font-semibold text-sm group-hover:text-yellow transition-colors duration-300">
                    Logout
                  </span>
                )}
              </div>
              <div className="pointer-events-none absolute inset-0 z-0 rounded-2xl bg-red/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestorSidebar;
